import { Link } from "@tanstack/react-router";
import { Eye, Calendar, Timer, FileText, Award } from "lucide-react";
import { AuthorVerificationBadge } from "@/components/agora/AgoraBadges";
import type { AgoraAuthorSummary, AgoraAuthorProfileExtras, TrustLevel } from "@/lib/agora/types";

/**
 * Bandeau auteur affiché en tête d'analyse. Objectif : valoriser dès le premier
 * écran la personne qui signe (photo, qualité, rang de contribution) et donner
 * les repères de lecture attendus (audience, parution, durée).
 * Rendu entièrement côté serveur : aucune donnée dépendante du navigateur.
 */

const TRUST_LABELS: Record<TrustLevel, string> = {
  standard: "Contributeur",
  verified: "Contributeur vérifié",
  trusted: "Contributeur de confiance",
  admin: "Rédaction Juriv'IA",
};

function initials(name: string) {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");
}

function formatCount(value: number) {
  return new Intl.NumberFormat("fr-FR").format(Math.max(0, value));
}

function formatDate(value: string | null | undefined) {
  if (!value) return null;
  return new Date(value).toLocaleDateString("fr-FR", {
    timeZone: "Europe/Paris",
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
}

function Stat({
  icon: Icon,
  label,
  value,
}: {
  icon: typeof Eye;
  label: string;
  value: string;
}) {
  return (
    <div className="flex items-center gap-2 rounded-lg border border-border bg-background/60 px-3 py-2">
      <Icon className="h-4 w-4 shrink-0 text-primary" aria-hidden />
      <span className="text-xs leading-tight text-muted-foreground">
        <span className="block font-semibold text-foreground">{value}</span>
        {label}
      </span>
    </div>
  );
}

export default function AuthorHero({
  author,
  profile,
  publishedAt,
  readingMinutes,
  viewsCount,
}: {
  author: AgoraAuthorSummary;
  profile?: AgoraAuthorProfileExtras | null;
  publishedAt: string | null;
  readingMinutes: number | null;
  /**
   * Lectures de CETTE analyse. `null` masque la statistique : en
   * prévisualisation, aucun compteur n'existe encore et afficher le cumul de
   * l'auteur laisserait croire que le brouillon a déjà été lu.
   */
  viewsCount: number | null;
}) {

  const published = formatDate(publishedAt);
  const rank = TRUST_LABELS[author.trust_level] ?? TRUST_LABELS.standard;
  const domains = profile?.domains?.slice(0, 3) ?? [];

  return (
    <section
      aria-label="Auteur de l'analyse"
      className="mt-6 rounded-2xl border border-border bg-card p-5"
    >
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
        <Link
          to="/auteurs/$slug"
          params={{ slug: author.slug }}
          aria-label={`Profil de ${author.display_name}`}
          className="shrink-0 self-start rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {author.avatar_url ? (
            <img
              src={author.avatar_url}
              alt={`Portrait de ${author.display_name}`}
              loading="lazy"
              width={96}
              height={96}
              className="h-20 w-20 rounded-xl border border-border object-cover sm:h-24 sm:w-24"
            />
          ) : (
            <span className="flex h-20 w-20 items-center justify-center rounded-xl border border-border bg-primary/10 text-xl font-semibold text-primary sm:h-24 sm:w-24">
              {initials(author.display_name)}
            </span>
          )}
        </Link>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <Link
              to="/auteurs/$slug"
              params={{ slug: author.slug }}
              /* Zone tactile ≥ 44 × 44 px sans décaler la ligne : padding
                 compensé par -my-2 et centré horizontalement pour conserver
                 l'alignement visuel. */
              className="-my-2 inline-flex min-h-11 min-w-11 items-center justify-center px-2 py-2 text-base font-semibold text-foreground hover:text-primary"
            >
              {author.display_name}
            </Link>

            <AuthorVerificationBadge
              status={author.verification_status}
              trustLevel={author.trust_level}
            />
            <span className="inline-flex items-center gap-1 rounded-full border border-border bg-muted/60 px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
              <Award className="h-3 w-3" aria-hidden />
              {rank}
            </span>
          </div>

          {(author.profession || author.organization_name) && (
            <p className="mt-1 text-sm text-muted-foreground">
              {[author.profession, author.organization_name].filter(Boolean).join(" · ")}
            </p>
          )}

          {profile?.bio && (
            <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-muted-foreground">
              {profile.bio}
            </p>
          )}

          {domains.length > 0 && (
            <ul className="mt-3 flex flex-wrap gap-1.5">
              {domains.map((domain) => (
                <li
                  key={domain}
                  className="rounded-full border border-primary/20 bg-primary/5 px-2 py-0.5 text-[11px] text-primary"
                >
                  {domain}
                </li>
              ))}
            </ul>
          )}

          <Link
            to="/auteurs/$slug"
            params={{ slug: author.slug }}
            className="mt-3 inline-flex min-h-11 items-center text-sm font-medium text-primary hover:underline md:min-h-0"
          >
            Voir le profil et toutes ses analyses
          </Link>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-2 border-t border-border pt-4 sm:grid-cols-4">
        {viewsCount === null ? null : (
          <Stat icon={Eye} label="lectures" value={formatCount(viewsCount)} />
        )}

        {published && <Stat icon={Calendar} label="1re parution" value={published} />}
        {readingMinutes ? (
          <Stat icon={Timer} label="de lecture" value={`${readingMinutes} min`} />
        ) : null}
        {profile ? (
          <Stat
            icon={FileText}
            label="analyses publiées"
            value={formatCount(profile.articles_count)}
          />
        ) : null}
      </div>
    </section>
  );
}
